import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

export type M2ingEnv = 'live' | 'staging' | 'dev';

interface EnvUrls {
  api: string; // .../api/
  storage: string; // .../uploads/
  short: string; // .../
}

/**
 * Einzige Quelle für die aktive Umgebung (Live / Staging / Dev) und deren URLs.
 * Ersetzt das frühere binäre `debug`-Flag + die Window-Globals in app.component.
 *
 * Die aktive Umgebung wird in Preferences (Key 'environment') persistiert und
 * beim App-Start via APP_INITIALIZER (siehe app.module) VOR dem ersten HTTP-Call
 * geladen. Ein Umgebungswechsel läuft über den Hidden-Menü-Flow (siehe home.page):
 * lokale Daten löschen -> neue Umgebung persistieren -> Logout -> Neu-Login.
 */
@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  static readonly PREF_KEY = 'environment';
  static readonly DEFAULT: M2ingEnv = 'live';

  static readonly CATALOG: { [k in M2ingEnv]: EnvUrls } = {
    live: {
      api: 'https://webservice.m2ing.com/api/',
      storage: 'https://webservice.m2ing.com/uploads/',
      short: 'https://webservice.m2ing.com/',
    },
    staging: {
      api: 'https://ws-staging.m2ing.com/api/',
      storage: 'https://ws-staging.m2ing.com/uploads/',
      short: 'https://ws-staging.m2ing.com/',
    },
    dev: {
      api: 'https://m2ing-admin.mmc-entwicklung.de/api/',
      storage: 'https://m2ing-admin.mmc-entwicklung.de/uploads/',
      short: 'https://m2ing-admin.mmc-entwicklung.de/',
    },
  };

  static readonly LABELS: { [k in M2ingEnv]: string } = {
    live: 'Live',
    staging: 'Staging',
    dev: 'Dev',
  };

  current: M2ingEnv = EnvironmentService.DEFAULT;

  /** Von APP_INITIALIZER aufgerufen: persistierte Umgebung laden (async, vor Bootstrap-Ende). */
  async load(): Promise<void> {
    try {
      const { value } = await Preferences.get({
        key: EnvironmentService.PREF_KEY,
      });
      if (value === 'live' || value === 'staging' || value === 'dev') {
        this.current = value;
      }
    } catch (e) {
      console.warn('EnvironmentService.load fehlgeschlagen, nutze Default', e);
    }
    console.log('Aktive Umgebung:', this.current, this.apiBase);
  }

  /**
   * Neue Umgebung persistieren. Bewusst getrennt vom Daten-Wipe: erst wipen,
   * DANN persistieren, damit dieser Key den Preferences.clear() überlebt.
   */
  async persist(env: M2ingEnv): Promise<void> {
    this.current = env;
    await Preferences.set({ key: EnvironmentService.PREF_KEY, value: env });
  }

  private get urls(): EnvUrls {
    return EnvironmentService.CATALOG[this.current];
  }

  get apiBase(): string {
    return this.urls.api;
  }

  get storageBase(): string {
    return this.urls.storage;
  }

  get storageShort(): string {
    return this.urls.short;
  }

  get label(): string {
    return EnvironmentService.LABELS[this.current];
  }

  get all(): M2ingEnv[] {
    return ['live', 'staging', 'dev'];
  }
}
